import { useEffect, useRef } from 'preact/hooks';
import { useAuth } from '../lib/auth';

export function SetupPage() {
  const { login, workspace, user, loading, error } = useAuth();
  const hasProcessedSession = useRef(false);
  const hasNavigated = useRef(false);
  const isWaitingForLogin = useRef(false);

  console.log('[Setup] Render - loading:', loading, 'user:', !!user, 'workspace:', !!workspace, 'waiting:', isWaitingForLogin.current, 'error:', error);

  // Single effect to handle both session processing and navigation
  useEffect(() => {
    // Step 1: Exchange one-time auth code from URL for session ID
    if (!hasProcessedSession.current) {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      // Legacy: still accept ?session= for in-flight redirects during deploy
      const legacySession = params.get('session');

      if (code || legacySession) {
        hasProcessedSession.current = true;
        isWaitingForLogin.current = true;
        // Clear URL params immediately
        window.history.replaceState({}, '', '/setup');

        if (code) {
          // Exchange one-time code for real session ID via API
          const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';
          fetch(`${API_URL}/v3/auth/exchange-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code })
          })
            .then(r => r.json())
            .then(data => {
              if (data.sessionId) {
                login(data.sessionId);
              } else {
                console.error('[Setup] Code exchange failed:', data.error);
              }
            })
            .catch(err => console.error('[Setup] Code exchange error:', err));
        } else {
          login(legacySession);
        }
        return;
      }
    }

    // Step 2: Handle navigation after auth state settles
    console.log('[Setup] Navigation check - loading:', loading, 'user:', !!user, 'workspace:', !!workspace, 'waiting:', isWaitingForLogin.current);

    // Don't navigate while loading or if already navigated
    if (loading || hasNavigated.current) {
      console.log('[Setup] Skipping navigation - loading or already navigated');
      return;
    }

    // Login completed successfully - navigate to dashboard
    if (user) {
      console.log('[Setup] User found, navigating to dashboard');
      hasNavigated.current = true;
      window.location.href = '/';
      return;
    }

    // Still waiting for login to complete
    if (isWaitingForLogin.current) {
      console.log('[Setup] Still waiting for login to complete');
      return;
    }

    // Auth finished loading, no user, not waiting - go to login
    if (workspace && !user) {
      console.log('[Setup] No user but workspace exists, navigating to login');
      hasNavigated.current = true;
      window.location.href = '/login';
      return;
    }
  }, [workspace, user, loading]);

  // Show error if initialization failed
  if (error) {
    return (
      <div class="loading-screen">
        <div class="error-icon">⚠️</div>
        <p>Something went wrong during setup.</p>
        <p class="error-detail">{error === 'initialization_failed' ? 'Failed to connect to the server.' : error}</p>
        <button
          class="btn btn-primary"
          onClick={() => window.location.reload()}
          style={{ marginTop: '1rem' }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div class="loading-screen">
      <div class="spinner" />
      <p>Setting up your workspace...</p>
      {isWaitingForLogin.current && <p class="setup-substatus">Validating your session...</p>}
    </div>
  );
}
